import {Injectable} from "@angular/core";
import {MatDialog} from "@angular/material/dialog";
import {SharedStateService} from "../shared-state.service";
import {SelectQuarterDialogComponent} from "../../portfolio-review/select-quarter-dialog/select-quarter-dialog.component";
import {Subscription} from "rxjs";
import * as htmlToImage from "html-to-image";
import { loadPdfMake } from "./export-libs";
import {Chart} from "chart.js";
import {ProductDTO} from "../user.service";
import {ArrayService} from "../array.service";
import {ActivatedRoute} from "@angular/router";

@Injectable({
  providedIn: 'root'
})
export class SavePageUtil {
  private loadingSubscription: Subscription | null = null;

  constructor(
    private readonly sharedStateService: SharedStateService,
    private readonly dialog: MatDialog,
    private readonly arrayService: ArrayService,
    private readonly route: ActivatedRoute
  ) {
  }

  public savePage(
    type: "CT - Product" | "CT - Portfolio" | "CT - Program" | "CT - Enabler",
    setLoading: (loading: boolean) => void,
    temporaryProduct: ProductDTO,
    userProducts: ProductDTO[],
    selectedProductId: number
  ): void {
    if (type === "CT - Product") {
      setLoading(true);
      this.sharedStateService.clearLoadingStatus();
      this.sharedStateService.setShowHiddenComponent(true);

      const loadingObservable = this.sharedStateService.isLoading$;

      this.loadingSubscription = loadingObservable.subscribe((loadingState) => {
        const isAllLoaded = Object.values(loadingState).every((status) => status === false) && Object.keys(loadingState).length > 0;

        if (isAllLoaded) {
          if (this.loadingSubscription) {
            this.loadingSubscription.unsubscribe();
          }
          this.capturePage(type, setLoading, temporaryProduct, userProducts, selectedProductId);
        }
      });
    } else if (type === "CT - Portfolio") {
      const dialogRef = this.dialog.open(SelectQuarterDialogComponent, {
        disableClose: true,
        width: "400px",
      });

      dialogRef.afterClosed().subscribe((selectedQuarter) => {
        if (selectedQuarter) {
          setLoading(true);

          const originalQuarters = {
            DeployIt: this.sharedStateService.getSelectedQuarter("DeployIt"),
            TeamConfidence: this.sharedStateService.getSelectedQuarter("TeamConfidence"),
            DeploymentProgress: this.sharedStateService.getSelectedQuarter("DeploymentProgress"),
          };
          this.sharedStateService.setSelectedQuarter("DeployIt", selectedQuarter);
          this.sharedStateService.setSelectedQuarter("TeamConfidence", selectedQuarter);
          this.sharedStateService.setSelectedQuarter("DeploymentProgress", selectedQuarter);

          this.sharedStateService.setShowHiddenComponentPortfolio(true);
          setTimeout(() => {
            this.capturePage(type, setLoading, temporaryProduct, userProducts, selectedProductId, selectedQuarter, originalQuarters);
          }, 3000);
        }
      });
    }
    else if (type === "CT - Program" || type === "CT - Enabler") {
      setLoading(true);
      this.sharedStateService.setShowHiddenComponent(true);
      this.sharedStateService.setShowHiddenComponentPortfolio(true);

      setTimeout(() => {
        this.capturePage(type, setLoading, null, null, null);
      }, 0);
    }
  }

  private capturePage(
    type: "CT - Product" | "CT - Portfolio" | "CT - Program" | "CT - Enabler",
    setLoading: (loading: boolean) => void,
    temporaryProduct: ProductDTO,
    userProducts: ProductDTO[],
    selectedProductId: number,
    selectedQuarter?: string,
    originalQuarters?: any
  ): void {
    const element = document.body;
    const hiddenComponent = document.getElementById("hidden-component");

    if (hiddenComponent) {
      hiddenComponent.style.visibility = "visible";
      hiddenComponent.style.height = "auto";
    }

    const visibleComponent = document.getElementById("visible-component");

    if (visibleComponent) {
      visibleComponent.style.visibility = "hidden";
      visibleComponent.style.height = "0";
    }

    const noExportElements = document.getElementsByClassName("no-export");
    for (const element of Array.from(noExportElements)) {
      (element as HTMLElement).style.display = "none";
    }

    const chartElement = Chart.getChart(`activityDistributionChart-${selectedQuarter}`);
    if (chartElement) {
      chartElement.options.scales.x.max = null;
      chartElement.update("none");
    }

    const actionPlanContainer = document.getElementById("action-plan-table-container");
    if(actionPlanContainer) {
      actionPlanContainer.style.overflow = "hidden";
      actionPlanContainer.style.maxHeight = "none";
    }


    const commentElements = document.getElementsByClassName("comment");
    for (const commentElement of Array.from(commentElements)) {
      (commentElement as HTMLElement).style.overflow = "hidden";
      (commentElement as HTMLElement).style.maxHeight = "none";
    }

    htmlToImage
      .toPng(element, {
        quality: 1,
        filter: (domNode) => {
          const className = typeof domNode.className === "string" ? domNode.className : "";
          return !className.includes("no-export") && !className.includes("no-export-loader");
        },
      })
      .then(async (dataUrl) => {
        if (type === "CT - Product") {
          const selectedProduct = temporaryProduct || userProducts.find((product) => product.id === selectedProductId);
          const branches = this.arrayService.formatArrayForDisplay(
            selectedProduct.branches,
            ""
          );

          const productName = selectedProduct.name || "";
          const currentDate = new Date().toISOString().split("T")[0];

          const link = document.createElement("a");
          link.href = dataUrl;
          link.download = `${currentDate} CT ${branches} ${productName}.png`;
          link.click();
        } else if (type === "CT - Portfolio") {
          const branch = this.route.firstChild.snapshot.paramMap.get("name")?.toUpperCase() || "UnknownBranch";
          const currentDate = new Date().toISOString().split("T")[0];

          const link = document.createElement("a");
          link.href = dataUrl;
          link.download = `${currentDate} CT ${branch} ${selectedQuarter}.png`;
          link.click();
        }
        else if (type === "CT - Program" || type === "CT - Enabler") {
          const pdfMake = await loadPdfMake();

          let img = new Image();
          img.src = dataUrl;

          img.onload = () => {
            let docDefinition = {
              pageSize: {
                width: img.width,
                height: img.height
              },
              pageMargins: [0, 0, 0, 0],
              content: [
                {
                  image: dataUrl,
                  width: img.width,
                  height: img.height
                }
              ],
              styles: {
                tableText: {
                  fontSize: 7,
                },
              },
            };

            pdfMake.createPdf(docDefinition).download(`${new Date().toISOString().split("T")[0]}_${type}.pdf`);
          };
        }

        if (hiddenComponent) {
          hiddenComponent.style.visibility = "hidden";
          hiddenComponent.style.height = "0";
        }

        if (visibleComponent) {
          visibleComponent.style.visibility = "visible";
          visibleComponent.style.height = "auto";
        }

        for (const element of Array.from(noExportElements)) {
          (element as HTMLElement).style.removeProperty("display");
        }

        if (chartElement) {
          chartElement.options.scales.x.max = 9;
          chartElement.update("none");
        }

        if (type == "CT - Portfolio") {
          this.sharedStateService.setSelectedQuarter("DeployIt", originalQuarters.DeployIt);
          this.sharedStateService.setSelectedQuarter("TeamConfidence", originalQuarters.TeamConfidence);
          this.sharedStateService.setSelectedQuarter("DeploymentProgress", originalQuarters.DeploymentProgress);
        } else {
          this.sharedStateService.setShowHiddenComponent(false);
        }
        setLoading(false);
        this.sharedStateService.setShowHiddenComponentPortfolio(false);
      })
      .catch((error) => {
        console.error("Error during the screen capture :", error);

        if (hiddenComponent) {
          hiddenComponent.style.visibility = "hidden";
          hiddenComponent.style.height = "0";
        }

        if (visibleComponent) {
          visibleComponent.style.visibility = "visible";
          visibleComponent.style.height = "auto";
        }

        for (const element of Array.from(noExportElements)) {
          (element as HTMLElement).style.removeProperty("display");
        }

        if (type === "CT - Portfolio") {
          this.sharedStateService.setShowHiddenComponentPortfolio(false);
        } else {
          this.sharedStateService.setShowHiddenComponent(false);
        }
        setLoading(false);
      });
  }
}
